import type { Swish, SwishData, User } from 'swish-core'
import { defineStore } from 'pinia'
import axios from 'axios'
import { jwtDecode } from "jwt-decode";

const versionString =
  import.meta.env.MODE === 'development' ? import.meta.env.VITE_APP_VERSION + '-dev' : import.meta.env.VITE_APP_VERSION

export const useStore = defineStore('main', {
  state: () => ({
    debug: import.meta.env.MODE === 'development',
    version: versionString,
    isInitialized: false,
    count: 0,
    swishes: [] as Swish[],
    selectedSwish: 'current',
    currentSwishData: null as SwishData | null,
    jwtToken: null as string | null,
    user: null as User | null,
    isAdmin: false
  }),

  actions: {
    async initApp() {
      this.isInitialized = true
      this.loadJwtToken()
      await this.fetchSwishes()
      await this.fetchCurrentSwishData()
      console.log('app initialized!')
    },
    loadJwtToken() {
      const token = localStorage.getItem('jwtToken')
      if (token) {
        this.jwtToken = token
        this.decodeJwtToken()
      }
    },
    async login(username: string, password: string): Promise<boolean> {
      try {
        const response = await axios.post('/api/auth/token', { username, password })
        if(response.status != 200)
          return false;
        this.jwtToken = response.data.token
        if (this.jwtToken) {
          localStorage.setItem('jwtToken', this.jwtToken)
          this.decodeJwtToken()
        }
        return true
      } catch (error) {
        console.error('[store] Login failed:', error)
        return false
      }
    },
    logout() {
      this.jwtToken = null
      this.user = null
      this.isAdmin = false
      localStorage.removeItem('jwtToken')
    },
    decodeJwtToken() {
      if (this.jwtToken) {
        try {
          const decodedToken: User = jwtDecode(this.jwtToken)
          this.user = decodedToken
          this.isAdmin = decodedToken.admin || false
        } catch (error) {
          console.error('Failed to decode JWT:', error)
          this.user = null
          this.isAdmin = false
        }
      } else {
        this.user = null
        this.isAdmin = false
      }
    },
    async fetchSwishes() {
      try {
        const response = await axios.get('/api/swishes', {
          headers: this.jwtToken ? { Authorization: `Bearer ${this.jwtToken}` } : {}
        })
        this.swishes = response.data
        console.log("fetched ", response.data)
      } catch (error) {
        console.error('Failed to fetch swishes:', error)
      }
    },
    async setSelectedSwish(swishId: string) {
      this.selectedSwish = swishId
      await this.fetchCurrentSwishData()
    },
    async fetchCurrentSwishData() {
      try {
        const currentTime =  Math.round(Date.now() / 1000 / 60) * 60;
        const response = await axios.get(`/api/swishes/${this.selectedSwish}/data/${currentTime}`, {
          headers: this.jwtToken ? { Authorization: `Bearer ${this.jwtToken}` } : {}
        })
        console.log("fetched ", response.data, this.selectedSwish)
        this.currentSwishData = response.data
      } catch (error) {
        console.error('Failed to fetch current swish data:', error)
      }
    },
  },

  getters: {
    isReady: (state) => {
      return !state.isInitialized
    }
  },
})
